import type { MouseEvent, ReactNode } from 'react'

type IconButtonProps = {
  /** Accessible name; also the tooltip. */
  label: string
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  children: ReactNode
  variant?: 'plain' | 'accent' | 'danger' | 'destructive'
  disabled?: boolean
  pressed?: boolean
  /**
   * A hairline ring around the painted circle. For controls that sit on a bare surface with nothing
   * behind them — the call bar — where a glyph alone reads as decoration (§10.7 item 6). It is
   * reinforcement, not the affordance: the glyph, the fill under a pointer and the accessible name
   * are the other three channels.
   */
  outlined?: boolean
  className?: string
  /** Diameter of the painted circle. The hit area is never smaller than 44px, whatever this says. */
  size?: number
}

const VARIANTS: Record<NonNullable<IconButtonProps['variant']>, string> = {
  plain: 'text-muted group-hover:text-ink group-hover:bg-surface2',
  accent: 'bg-accent text-on-accent group-hover:bg-accent-strong',
  danger: 'bg-danger text-on-danger group-hover:brightness-110',
  // A destructive action inside a list row: still red, but it does not shout from every row the way
  // a filled button does. The filled `danger` is for the one destructive action on a screen (end
  // the call, decline the ring, confirm a removal).
  destructive: 'text-danger group-hover:bg-danger/15',
}

/**
 * A 44px touch target that can still paint a 32px circle. The two used to be the same number, which
 * is why the old header packed five 36–44px controls into one row and the admin lists put 40px
 * destructive buttons next to a text button: WCAG 2.5.5 wants 44, the layout wanted less. Separate
 * them and both are satisfied.
 *
 * `.press` is the whole button's answer to a press: it scales to 0.97 over 120ms (§10.7 item 4).
 * The hit box scales with the circle, which is what a real press looks like; the movement is what
 * makes it feedback rather than a repaint.
 */
export function IconButton({
  label,
  onClick,
  children,
  variant = 'plain',
  disabled = false,
  pressed,
  outlined = false,
  className = '',
  size = 44,
}: IconButtonProps) {
  const hit = Math.max(44, size)
  const visual =
    variant === 'plain' && pressed
      ? 'bg-surface2 text-accent'
      : VARIANTS[variant]

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={`press group inline-flex shrink-0 items-center justify-center disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
      style={{ width: hit, height: hit }}
    >
      <span
        className={`flex items-center justify-center rounded-full ${outlined ? 'ring-1 ring-line-strong' : ''} ${visual}`}
        style={{ width: size, height: size }}
      >
        {children}
      </span>
    </button>
  )
}
